import type { Call } from "@frontierx/types";
import { useEffect, useState } from "react";
import { type CategorizedCalls, categorizeCalls } from "../utils/callFilters";

/**
 * Frontend-only call transition hook
 *
 * This approach is more efficient because:
 * 1. No backend calls needed - just re-categorizes existing data
 * 2. Immediate UI updates without network latency
 * 3. Works offline
 * 4. Reduces server load
 *
 * Updates every minute on the minute to ensure smooth transitions
 */
export function useFrontendCallTransitions(calls: Call[]): CategorizedCalls & {
  lastUpdated: Date;
} {
  const [categorizedCalls, setCategorizedCalls] = useState<CategorizedCalls>(
    () => categorizeCalls(calls),
  );
  const [lastUpdated, setLastUpdated] = useState(() => new Date());

  // Re-categorize when calls data changes
  useEffect(() => {
    const newCategorization = categorizeCalls(calls);
    setCategorizedCalls(newCategorization);
    setLastUpdated(new Date());
  }, [calls]);

  // Set up minute-based re-categorization for time transitions
  useEffect(() => {
    if (!calls.length) return;

    // Check if we have any calls that might transition
    const hasTimeTransitionCalls = calls.some((call) => {
      if (call.status !== "scheduled") return false;

      const startTime = new Date(call.startTime);
      const now = new Date();
      const timeDiff = startTime.getTime() - now.getTime();

      // Check for calls starting within next 2 hours or that started within last 2 hours
      return Math.abs(timeDiff) < 2 * 60 * 60 * 1000;
    });

    if (!hasTimeTransitionCalls) return;

    // Calculate time until next minute boundary
    const now = new Date();
    const nextMinute = new Date(now);
    nextMinute.setSeconds(0, 0);
    nextMinute.setMinutes(nextMinute.getMinutes() + 1);

    const timeUntilNextMinute = nextMinute.getTime() - now.getTime();

    // Store interval ID to ensure cleanup
    let intervalId: ReturnType<typeof setInterval> | null = null;

    // Set timeout to start interval at next minute boundary
    const initialTimeout = setTimeout(() => {
      // Update immediately when we hit the minute boundary
      const newCategorization = categorizeCalls(calls);
      setCategorizedCalls(newCategorization);
      setLastUpdated(new Date());

      // Then set up regular interval for every minute
      intervalId = setInterval(() => {
        const refreshedCategorization = categorizeCalls(calls);
        setCategorizedCalls(refreshedCategorization);
        setLastUpdated(new Date());
      }, 60 * 1000); // Every minute
    }, timeUntilNextMinute);

    // Cleanup function
    return () => {
      if (typeof clearTimeout !== "undefined") {
        clearTimeout(initialTimeout);
      }
      if (intervalId !== null) {
        clearInterval(intervalId);
      }
    };
  }, [calls]);

  return {
    ...categorizedCalls,
    lastUpdated,
  };
}
